// The analysis that produced a derived dataset: create (POST /studies/{study}/analyses) or edit
// (PATCH /analyses/{id}) with every field of the Analysis model (routers/analyses.py):
//   inputs       datasets of the project (any study, whoever wrote it);
//   instrument   SoftwareApplication (url + version required) or ComputationalWorkflow (url);
//   parameters   name and value (a unit is part of the value text, e.g. "0.05 FDR" or "4 °C");
//   agent        "Who ran it": me (the signed-in user), a colleague of the project (GET
//                /projects/{id}/people: its active members, no e-mail), or somebody
//                outside the portal (agent_user_id null, name + ORCID typed). Picking a person
//                fills name and ORCID from their account (the Account page's ORCID iD); both stay
//                editable. A recorded agent who is no longer listed (left the project) is offered
//                as "(recorded)", and an edit keeps them (the API accepts the unchanged id);
//   start / end  datetime-local in the browser's time zone, sent as ISO with offset;
//   protocol     the protocol FILE it followed (protocol_file_id): one of the project's files with
//                the role protocol (a protocol is a file since 2026-10-20; one request,
//                GET /projects/{id}/files?role=protocol; a failure is shown at the picker);
//   results      derived datasets of this dataset's study (preselected: this dataset).
// The form picks datasets; an input or result recorded as one FILE of a dataset (data_file_id, set
// through the API) is sent back unchanged while its dataset stays ticked (ioRefs).
import { useEffect, useMemo, useState } from "react";
import { api } from "../../../api";
import { useAuth } from "../../../auth/AuthContext";
import { ErrorNote, Loading } from "../../../components/Feedback";
import { Btn, Card, Field, Input, Select, Textarea } from "../../../components/Shared";
import { loadProjectDatasets } from "../projectDatasets";

const INSTRUMENT_TYPES = [
  { value: "SoftwareApplication", label: "Software (needs URL and version)" },
  { value: "ComputationalWorkflow", label: "Workflow (needs URL)" },
];

// ISO from the API -> "YYYY-MM-DDTHH:MM" in the browser's zone, and back.
export function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const EMPTY_INSTRUMENT = { type: "SoftwareApplication", name: "", version: "", url: "", repository: "",
                           container_image: "", commit: "" };

// "me" -> the signed-in user, "user:<id>" -> another portal user, "none" -> null.
function initialAgent(analysis, user) {
  if (!analysis) return "me";
  if (analysis.agent_user_id == null) return "none";
  return user && analysis.agent_user_id === user.id ? "me" : `user:${analysis.agent_user_id}`;
}

export function agentUserId(agent, user) {
  if (agent === "me") return user.id;
  return agent.startsWith("user:") ? Number(agent.slice(5)) : null;
}

// The refs to send for the ticked dataset ids: the refs recorded for a dataset (files included)
// when it was ticked already, else the whole dataset.
export function ioRefs(ids, recorded) {
  return ids.flatMap(id => {
    const kept = (recorded || []).filter(x => x.dataset_id === id && x.data_file_id != null);
    return kept.length ? kept.map(x => ({ dataset_id: id, data_file_id: x.data_file_id })) : [{ dataset_id: id }];
  });
}

const uniqueIds = refs => [...new Set(refs.map(o => o.dataset_id))];

function initialState(analysis, dataset, user) {
  if (analysis) {
    const instrument = { ...EMPTY_INSTRUMENT };
    Object.entries(analysis.instrument || {}).forEach(([k, v]) => { if (v != null) instrument[k] = v; });
    return {
      name: analysis.name || "", description: analysis.description || "",
      objects: uniqueIds(analysis.objects), results: uniqueIds(analysis.results),
      instrument,
      parameters: (analysis.parameters || []).map(x => ({ name: x.name || "", value: x.value })),
      agent: initialAgent(analysis, user),
      agent_name: analysis.agent_name || "", agent_orcid: analysis.agent_orcid || "",
      start_time: toLocalInput(analysis.start_time), end_time: toLocalInput(analysis.end_time),
      protocol: analysis.protocol_file_id ? String(analysis.protocol_file_id) : "",
    };
  }
  return {
    name: "", description: "", objects: [], results: [dataset.id],
    instrument: { ...EMPTY_INSTRUMENT },
    parameters: [], agent: "me",
    agent_name: (user && (user.full_name || user.username)) || "", agent_orcid: (user && user.orcid) || "",
    start_time: "", end_time: "", protocol: "",
  };
}

function CheckList({ legend, items, selected, onToggle }) {
  return (
    <fieldset style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 12px", margin: 0 }}>
      <legend style={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase",
                       letterSpacing: "0.05em" }}>{legend}</legend>
      {items.length === 0 && <p style={{ fontSize: 13, color: "#6b7280", margin: 4 }}>None to choose from.</p>}
      {items.map(it => (
        <label key={it.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, padding: "2px 0" }}>
          <input type="checkbox" checked={selected.includes(it.id)} onChange={() => onToggle(it.id)} />
          <span>{it.name}</span>
          <span style={{ fontSize: 12, color: "#6b7280" }}>{it.hint}</span>
        </label>
      ))}
    </fieldset>
  );
}

export default function AnalysisForm({ dataset, analysis, onSaved, onCancel }) {
  const { user } = useAuth();
  const [form, setForm] = useState(() => initialState(analysis, dataset, user));
  const [projectDatasets, setProjectDatasets] = useState(null);
  const [protocolFiles, setProtocolFiles] = useState(null);   // [{id, filename, dataset_name}]
  const [protocolError, setProtocolError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Every dataset of the project: its studies, then each study's datasets.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await loadProjectDatasets(dataset.project_id);
        if (!cancelled) setProjectDatasets(all);
      } catch (e) {
        if (!cancelled) setLoadError(e);
      }
    })();
    return () => { cancelled = true; };
  }, [dataset.project_id]);

  // The project's protocol files, in one request. Without them the form still saves (and keeps
  // a recorded one); the error is shown at the picker, never taken for "there are none".
  useEffect(() => {
    let cancelled = false;
    setProtocolError(null);
    api.get(`/projects/${dataset.project_id}/files?role=protocol`).then(
      fs => { if (!cancelled) setProtocolFiles(fs || []); },
      e => { if (!cancelled) { setProtocolFiles([]); setProtocolError(e); } });
    return () => { cancelled = true; };
  }, [dataset.project_id]);

  // Who can be named as the agent. Without the list the form still offers me / outside.
  const [people, setPeople] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get(`/projects/${dataset.project_id}/people`)
      .then(r => { if (!cancelled) setPeople(r || []); }, () => {});
    return () => { cancelled = true; };
  }, [dataset.project_id]);

  // The project's protocol files; the recorded one stays offered when its role changed since.
  const protocolOptions = useMemo(() => {
    const out = (protocolFiles || []).map(f => ({ value: String(f.id), label: `${f.filename} (${f.dataset_name})` }));
    if (form.protocol && !out.some(o => o.value === form.protocol) && analysis && analysis.protocol_file_id) {
      out.push({ value: form.protocol, label: `${analysis.protocol_filename || `File ${analysis.protocol_file_id}`} (recorded)` });
    }
    return out;
  }, [protocolFiles, form.protocol, analysis]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setInstrument = (k, v) => setForm(f => ({ ...f, instrument: { ...f.instrument, [k]: v } }));
  const toggle = k => id => setForm(f => ({ ...f, [k]: f[k].includes(id) ? f[k].filter(x => x !== id) : [...f[k], id] }));
  const setParam = (i, patch) => setForm(f => ({ ...f, parameters: f.parameters.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));

  if (loadError) return <ErrorNote error={loadError} />;
  if (!projectDatasets) return <Loading label="Loading the project's datasets…" />;

  // me, the colleagues (the signed-in user is "me", not listed twice), a recorded agent no longer
  // listed, and somebody outside the portal.
  const colleagues = people.filter(p => !user || p.id !== user.id);
  const recordedId = analysis && analysis.agent_user_id;
  const agentOptions = [
    { value: "me", label: `Me (${user ? (user.full_name || user.username) : "signed-in user"})` },
    ...colleagues.map(p => ({ value: `user:${p.id}`, label: p.name })),
    ...(recordedId != null && (!user || recordedId !== user.id) && !colleagues.some(p => p.id === recordedId)
      ? [{ value: `user:${recordedId}`, label: `${analysis.agent_name || `#${recordedId}`} (recorded)` }] : []),
    { value: "none", label: "Somebody outside the portal" },
  ];
  // Picking a person fills their name and ORCID; "outside" empties both for typing.
  const pickAgent = agent => {
    const id = agentUserId(agent || "none", user);
    let name = "";
    let orcid = "";
    if (agent === "me") {
      name = user.full_name || user.username;
      orcid = user.orcid || "";
    } else if (id != null) {
      const p = people.find(x => x.id === id);
      const recorded = p ? null : analysis;
      name = p ? p.name : (recorded && recorded.agent_name) || "";
      orcid = (p ? p.orcid : recorded && recorded.agent_orcid) || "";
    }
    setForm(f => ({ ...f, agent: agent || "none", agent_name: name, agent_orcid: orcid }));
  };

  const inputs = projectDatasets.map(d => ({ id: d.id, name: d.name, hint: `${d.study_name} · ${d.kind}` }));
  const results = projectDatasets.filter(d => d.study_id === dataset.study_id && d.kind === "derived")
    .map(d => ({ id: d.id, name: d.name, hint: "derived" }));

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const instrument = Object.fromEntries(Object.entries(form.instrument).filter(([, v]) => v !== ""));
    const body = {
      name: form.name, description: form.description || null,
      instrument,
      parameters: form.parameters.filter(p => p.name).map(p => Object.fromEntries(
        Object.entries(p).filter(([, v]) => v !== "" && v != null))),
      agent_user_id: agentUserId(form.agent, user),
      agent_name: form.agent_name || null, agent_orcid: form.agent_orcid || null,
      start_time: fromLocalInput(form.start_time), end_time: fromLocalInput(form.end_time),
      protocol_file_id: form.protocol ? Number(form.protocol) : null,
      objects: ioRefs(form.objects, analysis && analysis.objects),
      results: ioRefs(form.results, analysis && analysis.results),
    };
    try {
      const saved = analysis
        ? await api.patch(`/analyses/${analysis.id}`, body)
        : await api.post(`/studies/${dataset.study_id}/analyses`, body);
      onSaved(saved);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 };
  return (
    <Card style={{ marginBottom: 20 }}>
      <form onSubmit={save} aria-label={analysis ? `Edit analysis ${analysis.name}` : "Record the analysis"}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <h3 style={{ margin: 0, fontSize: 17 }}>{analysis ? `Edit analysis: ${analysis.name}` : "Record the analysis"}</h3>
        <div style={grid}>
          <Field label="Analysis name" required>
            <Input value={form.name} onChange={e => set("name", e.target.value)} />
          </Field>
          <Field label="Description">
            <Textarea value={form.description} onChange={e => set("description", e.target.value)} />
          </Field>
        </div>

        <CheckList legend="Inputs (datasets of the project)" items={inputs} selected={form.objects}
                   onToggle={toggle("objects")} />

        <div style={grid}>
          <Field label="Instrument type">
            <Select options={INSTRUMENT_TYPES} value={form.instrument.type}
                    onChange={e => setInstrument("type", e.target.value || "SoftwareApplication")} />
          </Field>
          <Field label="Instrument name" required>
            <Input value={form.instrument.name} onChange={e => setInstrument("name", e.target.value)} />
          </Field>
          <Field label="Version" required={form.instrument.type === "SoftwareApplication"}>
            <Input value={form.instrument.version} onChange={e => setInstrument("version", e.target.value)} />
          </Field>
          <Field label="URL" required>
            <Input type="url" value={form.instrument.url} onChange={e => setInstrument("url", e.target.value)} />
          </Field>
          <Field label="Repository">
            <Input value={form.instrument.repository} onChange={e => setInstrument("repository", e.target.value)} />
          </Field>
          <Field label="Container image">
            <Input value={form.instrument.container_image}
                   onChange={e => setInstrument("container_image", e.target.value)} />
          </Field>
          <Field label="Commit">
            <Input value={form.instrument.commit} onChange={e => setInstrument("commit", e.target.value)} />
          </Field>
        </div>

        <fieldset style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 12px", margin: 0 }}>
          <legend style={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase",
                           letterSpacing: "0.05em" }}>Parameters</legend>
          {form.parameters.map((p, i) => (
            <div key={i} style={{ ...grid, alignItems: "end", borderBottom: "1px solid #f3f4f6", padding: "8px 0" }}>
              <Field label={`Parameter ${i + 1} name`}>
                <Input value={p.name || ""} onChange={e => setParam(i, { name: e.target.value })} />
              </Field>
              <Field label={`Parameter ${i + 1} value`}>
                <Input value={p.value || ""} onChange={e => setParam(i, { value: e.target.value })} />
              </Field>
              <Btn variant="secondary" onClick={() => setForm(f => ({ ...f, parameters: f.parameters.filter((_, j) => j !== i) }))}>
                Remove parameter {i + 1}
              </Btn>
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setForm(f => ({ ...f, parameters: [...f.parameters, { name: "" }] }))}>
              Add parameter
            </Btn>
          </div>
        </fieldset>

        <div style={grid}>
          <Field label="Who ran it" hint="you, a colleague of the project, or somebody outside the portal">
            <Select options={agentOptions} value={form.agent} onChange={e => pickAgent(e.target.value)} />
          </Field>
          <Field label="Agent name">
            <Input value={form.agent_name} onChange={e => set("agent_name", e.target.value)} />
          </Field>
          <Field label="Agent ORCID" hint="e.g. 0000-0002-1825-0097">
            <Input value={form.agent_orcid} onChange={e => set("agent_orcid", e.target.value)} />
          </Field>
          <Field label="Start time">
            <Input type="datetime-local" value={form.start_time} onChange={e => set("start_time", e.target.value)} />
          </Field>
          <Field label="End time">
            <Input type="datetime-local" value={form.end_time} onChange={e => set("end_time", e.target.value)} />
          </Field>
          <Field label="Protocol file" hint="a file of the project with the role protocol (set it on a dataset's Files tab)">
            <Select options={protocolOptions} value={form.protocol} onChange={e => set("protocol", e.target.value)} />
            {protocolError && (
              <div style={{ marginTop: 4 }}>
                <ErrorNote error={protocolError} />
                <span style={{ fontSize: 12 }}>The project's protocol files could not be loaded; reopen the form to try again.</span>
              </div>
            )}
          </Field>
        </div>

        <CheckList legend="Results (derived datasets of this study)" items={results} selected={form.results}
                   onToggle={toggle("results")} />

        <ErrorNote error={error} />
        <div style={{ display: "flex", gap: 8 }}>
          <Btn type="submit" disabled={saving || !form.name || !form.instrument.name}>
            {saving ? "Saving…" : "Save analysis"}
          </Btn>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>
      </form>
    </Card>
  );
}
